import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchAdminDraft, fetchAdminSession, loginAdmin, logoutAdmin, publishAdminDraft, saveAdminDraft, uploadAdminImage } from '../services/workspaceApi';
import type { AdminSession, MediaAsset, MediaKind, RestaurantWorkspace } from '../types/workspace';
import { normalizeWorkspacePayload } from '../utils/workspaceHelpers';

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';
type DraftStatus = 'idle' | 'loading' | 'ready' | 'error';

interface DraftMetaState {
  draftUpdatedAt?: string;
  publishedAt?: string;
  hasUnpublishedChanges: boolean;
}

interface AdminContextValue {
  session: AdminSession | null;
  sessionStatus: SessionStatus;
  draftWorkspace: RestaurantWorkspace | null;
  draftStatus: DraftStatus;
  draftError: string | null;
  dirty: boolean;
  saving: boolean;
  publishing: boolean;
  saveMessage: string | null;
  draftMeta: DraftMetaState;
  login: (passcode: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshDraft: () => Promise<void>;
  replaceWorkspace: (workspace: RestaurantWorkspace) => void;
  updateWorkspace: (updater: (workspace: RestaurantWorkspace) => RestaurantWorkspace) => void;
  saveDraft: () => Promise<void>;
  publishDraft: () => Promise<void>;
  uploadImage: (file: File, kind: MediaKind, altText: string) => Promise<MediaAsset>;
}

const defaultMeta: DraftMetaState = {
  hasUnpublishedChanges: false,
};

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('loading');
  const [draftWorkspace, setDraftWorkspace] = useState<RestaurantWorkspace | null>(null);
  const [draftStatus, setDraftStatus] = useState<DraftStatus>('idle');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [draftMeta, setDraftMeta] = useState<DraftMetaState>(defaultMeta);

  const refreshDraft = async () => {
    setDraftStatus('loading');
    try {
      const response = await fetchAdminDraft();
      setDraftWorkspace(normalizeWorkspacePayload(response.workspace));
      setDraftMeta({
        draftUpdatedAt: response.draftUpdatedAt,
        publishedAt: response.publishedAt,
        hasUnpublishedChanges: response.hasUnpublishedChanges,
      });
      setDirty(false);
      setDraftError(null);
      setDraftStatus('ready');
    } catch (caughtError) {
      setDraftStatus('error');
      setDraftError(caughtError instanceof Error ? caughtError.message : 'Could not load draft workspace');
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const nextSession = await fetchAdminSession();
        setSession(nextSession);
        if (nextSession.authenticated) {
          setSessionStatus('authenticated');
          await refreshDraft();
          return;
        }
        setSessionStatus('unauthenticated');
      } catch {
        setSessionStatus('unauthenticated');
      }
    };

    void bootstrap();
  }, []);

  const login = async (passcode: string) => {
    const nextSession = await loginAdmin(passcode);
    setSession(nextSession);
    setSessionStatus('authenticated');
    await refreshDraft();
  };

  const logout = async () => {
    await logoutAdmin();
    setSession(null);
    setSessionStatus('unauthenticated');
    setDraftWorkspace(null);
    setDirty(false);
    setDraftStatus('idle');
    setDraftError(null);
    setSaveMessage(null);
    setDraftMeta(defaultMeta);
  };

  const replaceWorkspace = (workspace: RestaurantWorkspace) => {
    setDraftWorkspace(workspace);
    setDirty(true);
    setSaveMessage(null);
  };

  const updateWorkspace = (updater: (workspace: RestaurantWorkspace) => RestaurantWorkspace) => {
    setDraftWorkspace((currentWorkspace) => {
      if (!currentWorkspace) {
        return currentWorkspace;
      }
      return updater(currentWorkspace);
    });
    setDirty(true);
    setSaveMessage(null);
  };

  const saveDraft = async () => {
    if (!draftWorkspace) {
      return;
    }

    setSaving(true);
    setDraftError(null);
    try {
      const response = await saveAdminDraft(draftWorkspace);
      setDraftWorkspace(normalizeWorkspacePayload(response.workspace));
      setDraftMeta({
        draftUpdatedAt: response.draftUpdatedAt,
        publishedAt: response.publishedAt,
        hasUnpublishedChanges: response.hasUnpublishedChanges,
      });
      setDirty(false);
      setSaveMessage('Draft saved');
    } catch (caughtError) {
      setDraftError(caughtError instanceof Error ? caughtError.message : 'Could not save draft workspace');
      throw caughtError;
    } finally {
      setSaving(false);
    }
  };

  const publishDraft = async () => {
    if (!draftWorkspace) {
      return;
    }

    setPublishing(true);
    setDraftError(null);
    try {
      if (dirty) {
        await saveDraft();
      }

      const response = await publishAdminDraft();
      setDraftWorkspace(normalizeWorkspacePayload(response.workspace));
      setDraftMeta({
        draftUpdatedAt: response.draftUpdatedAt,
        publishedAt: response.publishedAt,
        hasUnpublishedChanges: response.hasUnpublishedChanges,
      });
      setDirty(false);
      setSaveMessage('Draft published to the live menu');
    } catch (caughtError) {
      setDraftError(caughtError instanceof Error ? caughtError.message : 'Could not publish draft workspace');
      throw caughtError;
    } finally {
      setPublishing(false);
    }
  };

  const uploadImageAndStore = useCallback(async (file: File, kind: MediaKind, altText: string) => {
    const asset = await uploadAdminImage(file, kind, altText);
    setDraftWorkspace((currentWorkspace) => {
      if (!currentWorkspace) {
        return currentWorkspace;
      }

      const nextMedia = [
        asset,
        ...currentWorkspace.mediaAssets.filter((existingAsset) => existingAsset.id !== asset.id),
      ];

      return {
        ...currentWorkspace,
        mediaAssets: nextMedia,
      };
    });
    setDirty(true);
    setSaveMessage(null);
    return asset;
  }, []);

  const value: AdminContextValue = {
    session,
    sessionStatus,
    draftWorkspace,
    draftStatus,
    draftError,
    dirty,
    saving,
    publishing,
    saveMessage,
    draftMeta,
    login,
    logout,
    refreshDraft,
    replaceWorkspace,
    updateWorkspace,
    saveDraft,
    publishDraft,
    uploadImage: uploadImageAndStore,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdminContext = () => {
  const context = useContext(AdminContext);

  if (!context) {
    throw new Error('useAdminContext must be used within AdminProvider');
  }

  return context;
};
